import React, { useState } from 'react';
import DisModal from './design-system/DisModal';
import DisButton from './design-system/DisButton';
import PlanLimitModal from './PlanLimitModal';

const AddChildModal = ({ isOpen, onClose, onAddChild, onUpgrade }) => {
  const [formData, setFormData] = useState({
    name: '',
    birthMonth: '',
    birthYear: '',
    gender: 'neutral'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPlanLimitModal, setShowPlanLimitModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const calculateAge = (birthMonth, birthYear) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    let age = currentYear - parseInt(birthYear);
    
    if (currentMonth < parseInt(birthMonth)) {
      age--;
    }
    
    return age;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le prénom est requis';
    }
    
    if (!formData.birthMonth || formData.birthMonth < 1 || formData.birthMonth > 12) {
      newErrors.birthMonth = 'Sélectionnez un mois';
    }
    
    if (!formData.birthYear) {
      newErrors.birthYear = 'Sélectionnez une année';
    } else {
      const currentYear = new Date().getFullYear();
      const minYear = currentYear - 18;
      const maxYear = currentYear - 1;
      
      if (formData.birthYear < minYear || formData.birthYear > maxYear) {
        newErrors.birthYear = `Année entre ${minYear} et ${maxYear}`;
      }
    }
    
    if (formData.birthMonth && formData.birthYear && !newErrors.birthMonth && !newErrors.birthYear) {
      const calculatedAge = calculateAge(formData.birthMonth, formData.birthYear);
      if (calculatedAge < 1 || calculatedAge > 18) {
        newErrors.birthYear = 'L\'enfant doit avoir entre 1 et 18 ans';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const calculatedAge = calculateAge(formData.birthMonth, formData.birthYear);
      
      await onAddChild({
        name: formData.name.trim(),
        age: calculatedAge,
        birth_month: parseInt(formData.birthMonth),
        birth_year: parseInt(formData.birthYear),
        gender: formData.gender
      });
      
      // Reset form data (le parent gère la fermeture)
      setFormData({ name: '', birthMonth: '', birthYear: '', gender: 'neutral' });
      setErrors({});
      // Note: onClose() est appelé par le parent (ReassuringChildPrompt.handleRealChildCreated)
    } catch (error) {
      console.error('Error adding child:', error);
      
      const errorData = error.response?.data?.detail;
      if (typeof errorData === 'object' && errorData.error === 'child_limit_reached') {
        onClose();
        setShowPlanLimitModal(true);
        return;
      }
      
      if (error.message && error.message.includes('Limite de profils enfants atteinte')) {
        onClose();
      } else {
        const errorMessage = error.message || 'Erreur lors de l\'ajout du profil';
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', birthMonth: '', birthYear: '', gender: 'neutral' });
    setErrors({});
    onClose();
  };

  const months = [
    { value: '1', label: 'Janvier' },
    { value: '2', label: 'Février' },
    { value: '3', label: 'Mars' },
    { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' },
    { value: '8', label: 'Août' },
    { value: '9', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' }
  ];

  return (
    <>
      <DisModal
        isOpen={isOpen}
        onClose={handleClose}
        title=""
        size="md"
        transparent={true}
      >
        {/* Header épuré */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">👶</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Nouveau profil enfant
          </h2>
          <p className="text-gray-600 text-sm">
            Quelques infos pour personnaliser l'expérience
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Prénom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prénom
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Emma, Lucas, Zoé..."
              className={`
                w-full px-4 py-3 rounded-xl border-2 transition-all duration-200
                focus:outline-none focus:ring-0
                ${errors.name 
                  ? 'border-red-300 bg-red-50 focus:border-red-400' 
                  : 'border-gray-200 focus:border-teal-400 bg-white/80'
                }
              `}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          {/* Date de naissance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de naissance
            </label>
            <div className="grid grid-cols-2 gap-3">
              <select
                name="birthMonth"
                value={formData.birthMonth}
                onChange={handleChange}
                className={`
                  w-full px-4 py-3 rounded-xl border-2 transition-all duration-200
                  focus:outline-none focus:ring-0 appearance-none bg-white/80
                  ${errors.birthMonth 
                    ? 'border-red-300 bg-red-50 focus:border-red-400' 
                    : 'border-gray-200 focus:border-teal-400'
                  }
                `}
              >
                <option value="">Mois</option>
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>

              <select
                name="birthYear"
                value={formData.birthYear}
                onChange={handleChange}
                className={`
                  w-full px-4 py-3 rounded-xl border-2 transition-all duration-200
                  focus:outline-none focus:ring-0 appearance-none bg-white/80
                  ${errors.birthYear 
                    ? 'border-red-300 bg-red-50 focus:border-red-400' 
                    : 'border-gray-200 focus:border-teal-400'
                  }
                `}
              >
                <option value="">Année</option>
                {Array.from({ length: 18 }, (_, i) => {
                  const year = new Date().getFullYear() - i - 1;
                  return (
                    <option key={year} value={year}>{year}</option>
                  );
                })}
              </select>
            </div>
            {(errors.birthMonth || errors.birthYear) && (
              <p className="text-red-500 text-xs mt-1">{errors.birthMonth || errors.birthYear}</p>
            )}
          </div>

          {/* Âge calculé */}
          {formData.birthMonth && formData.birthYear && !errors.birthMonth && !errors.birthYear && (
            <div className="bg-teal-50/80 backdrop-blur-sm text-teal-700 px-4 py-3 rounded-xl text-center">
              <span className="font-medium">{calculateAge(formData.birthMonth, formData.birthYear)} ans</span>
            </div>
          )}

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Genre <span className="text-gray-400 font-normal">(pour personnaliser les réponses)</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="girl"
                  checked={formData.gender === 'girl'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`
                  p-4 rounded-xl border-2 transition-all duration-200 text-center
                  ${formData.gender === 'girl' 
                    ? 'border-cyan-400 bg-cyan-50/80 shadow-md scale-105' 
                    : 'border-gray-200 bg-white/60 hover:border-gray-300 hover:bg-white/80'
                  }
                `}>
                  <div className="text-3xl mb-1">👧</div>
                  <span className={`text-sm font-medium ${formData.gender === 'girl' ? 'text-cyan-600' : 'text-gray-600'}`}>
                    Fille
                  </span>
                </div>
              </label>
              
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="boy"
                  checked={formData.gender === 'boy'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`
                  p-4 rounded-xl border-2 transition-all duration-200 text-center
                  ${formData.gender === 'boy' 
                    ? 'border-blue-400 bg-blue-50/80 shadow-md scale-105' 
                    : 'border-gray-200 bg-white/60 hover:border-gray-300 hover:bg-white/80'
                  }
                `}>
                  <div className="text-3xl mb-1">👦</div>
                  <span className={`text-sm font-medium ${formData.gender === 'boy' ? 'text-blue-600' : 'text-gray-600'}`}>
                    Garçon
                  </span>
                </div>
              </label>
              
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="neutral"
                  checked={formData.gender === 'neutral'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`
                  p-4 rounded-xl border-2 transition-all duration-200 text-center
                  ${formData.gender === 'neutral' 
                    ? 'border-teal-400 bg-teal-50/80 shadow-md scale-105' 
                    : 'border-gray-200 bg-white/60 hover:border-gray-300 hover:bg-white/80'
                  }
                `}>
                  <div className="text-3xl mb-1">😊</div>
                  <span className={`text-sm font-medium ${formData.gender === 'neutral' ? 'text-teal-600' : 'text-gray-600'}`}>
                    Neutre
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Erreur submit */}
          {errors.submit && (
            <div className="bg-red-50/90 backdrop-blur-sm border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm text-center">
              {errors.submit}
            </div>
          )}

          {/* Boutons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-medium
                hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-teal-500 
                text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200
                disabled:opacity-50 hover:from-blue-600 hover:to-teal-600"
            >
              {loading ? 'Création...' : 'Créer le profil'}
            </button>
          </div>
        </form>
      </DisModal>

      <PlanLimitModal
        isOpen={showPlanLimitModal}
        onClose={() => setShowPlanLimitModal(false)}
        onUpgrade={() => {
          setShowPlanLimitModal(false);
          if (onUpgrade) {
            onUpgrade();
          }
        }}
        limitType="children"
      />
    </>
  );
};

export default AddChildModal;
